import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
	Component,
	computed,
	DOCUMENT,
	HostListener,
	inject,
	input,
	PLATFORM_ID,
	signal,
	viewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { DynamicModule } from 'ng-dynamic-component';
import { MarkdownModule } from 'ngx-markdown';
import { firstValueFrom, take } from 'rxjs';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import type { AlertType } from '@valk-nx/components/ui-alert/src/lib/contracts/alert.types';
import {
	ALERT_SUCCESS,
	ALERT_WARNING,
} from '@valk-nx/components/ui-alert/src/lib/contracts/alert.types';
import { StorytellingComponent } from '@valk-nx/components/ui-storytelling/src/lib/storytelling';
import type { Language } from '@valk-nx/core/lib/core';
import { LANGUAGE, toQueryString } from '@valk-nx/core/lib/core';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { RouteHelper } from '@valk-nx/router-store/router.helper';
import { AccordionComponent } from '@valk-nx/storyblok/components/accordion/src/lib/accordion';
import { StoryblokCardSectionComponent } from '@valk-nx/storyblok/components/card-section/src/lib/card-section';
import { HeadingGroupComponent } from '@valk-nx/storyblok/components/heading-group/src/lib/heading-group';
import { ImageComponent } from '@valk-nx/storyblok/components/image/src/lib/image';
import { ReferenceComponent } from '@valk-nx/storyblok/components/reference/src/lib/reference.component';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { StoryblokSearchableListSectionComponent } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section';
import type { ExtendedPersuadeCardInputs } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section.interface';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { PageSettingsStore } from '@valk-nx/storyblok-store/src/lib/store/page-settings/page-settings.store';
import type { PromotionCodeInterface } from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import type {
	CardsSection,
	HeadingGroupSection,
	ImageWithoutAspectRatio,
	ISbStoryBlok,
	StoryblokReference,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { PromotionCodePromoFormComponent } from './promo-form/promotion-code-promo-form';

const Components = {
	imageWithoutAspectRatio: ImageComponent,
	reference: ReferenceComponent,
};

interface PrettyPromotionCodeDates {
	bookableFrom: string;
	bookableTo: string;
	visibleFrom: string;
	visibleTo: string;
}

interface PromotionCodeStatus {
	translationKey: string;
	type: AlertType;
}

@Component({
	selector: 'sb-promotion-code',
	templateUrl: './promotion-code.component.html',
	imports: [
		AccordionComponent,
		AlertComponent,
		DynamicModule,
		HeadingGroupComponent,
		MarkdownModule,
		PromotionCodePromoFormComponent,
		ReferenceComponent,
		RichTextComponent,
		StoryblokCardSectionComponent,
		StoryblokSearchableListSectionComponent,
		StorytellingComponent,
		TranslatePipe,
	],
})
export class PromotionCodeComponent
	extends StoryblokRootDirective
	implements OnInit
{
	private readonly pageSettingsStore = inject(PageSettingsStore);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly document = inject(DOCUMENT);
	private readonly bookingtool = inject(BOOKINGTOOL);
	private readonly location = inject(Location);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly defaultLanguage = inject<Language>(LANGUAGE);

	private readonly promoFormComponent =
		viewChild<PromotionCodePromoFormComponent>('promoForm');

	cardsSection = input.required<CardsSection[]>();
	image = input.required<ImageWithoutAspectRatio[]>();
	reference = input.required<StoryblokReference[]>();

	promotionCodeData = signal<PromotionCodeInterface | undefined>(undefined);
	promotionCode = signal<string>('');

	readonly cards = computed<ISbStoryBlok[]>(() => {
		if (this.cardsSection()?.length === 0) {
			return [];
		}
		return this.cardsSection()[0].cards;
	});

	readonly cardsTitle = computed<HeadingGroupSection[]>(() => {
		if (this.cardsSection()?.length === 0) {
			return [];
		}
		return this.cardsSection()[0].title;
	});

	readonly storytellingImage = computed<PhotoInterface | null>(() => {
		if (this.image()?.length === 0) {
			return null;
		}
		return StoryblokHelper.mapBynderToPhotoInterface(
			this.image()[0].bynder[0],
		);
	});

	readonly prettyPromotionDates = computed<PrettyPromotionCodeDates>(() => {
		const promoData = this.promotionCodeData();
		const prettyDateFormat = 'D MMMM YYYY';

		return {
			bookableFrom: promoData?.bookable?.from
				? dayjs(promoData.bookable.from).format(prettyDateFormat)
				: '',
			bookableTo: promoData?.bookable?.to
				? dayjs(promoData.bookable.to).format(prettyDateFormat)
				: '',
			visibleFrom: promoData?.visible?.from
				? dayjs(promoData.visible.from).format(prettyDateFormat)
				: '',
			visibleTo: promoData?.visible?.to
				? dayjs(promoData.visible.to).format(prettyDateFormat)
				: '',
		};
	});

	readonly promotionCodeStatus = computed<PromotionCodeStatus | null>(() => {
		const promoData = this.promotionCodeData();

		if (!promoData) {
			return null;
		}

		const isBookable =
			promoData.visible?.from &&
			dayjs().isSameOrAfter(dayjs(promoData.visible.from));

		const hasDiscount = !!promoData.discount;
		const status = `${isBookable ? 'bookable' : 'unbookable'}-${hasDiscount ? 'discount' : 'no-discount'}`;

		switch (status) {
			case 'unbookable-discount':
				return {
					translationKey:
						'storyblok.pages.promotion-code.form.promo-code.activated.unbookable.discount-description',
					type: ALERT_WARNING,
				};
			case 'unbookable-no-discount':
				return {
					translationKey:
						'storyblok.pages.promotion-code.form.promo-code.activated.unbookable.no-discount-description',
					type: ALERT_WARNING,
				};
			case 'bookable-discount':
				return {
					translationKey:
						'storyblok.pages.promotion-code.form.promo-code.activated.bookable.discount-description',
					type: ALERT_SUCCESS,
				};
			case 'bookable-no-discount':
			default:
				return {
					translationKey:
						'storyblok.pages.promotion-code.form.promo-code.activated.bookable.no-discount-description',
					type: ALERT_SUCCESS,
				};
		}
	});

	readonly mappedPromoHotels = computed<ExtendedPersuadeCardInputs[]>(() => {
		const hotels = this.promotionCodeData()?.hotels;
		if (hotels.length > 0) {
			return hotels.map((hotel) => {
				const isBookable =
					hotel.visible?.from &&
					dayjs().isSameOrAfter(dayjs(hotel.visible.from));

				return {
					cardUrl: hotel.toUrl,
					content: hotel.usps?.length
						? `- ${hotel.usps.join('\n- ')}`
						: '',
					ctaTarget: '_blank',
					ctaText:
						isBookable &&
						this.translate.instant('global.book-directly'),
					ctaType: 'btn-primary',
					ctaUrl:
						isBookable &&
						this.getBookingUrl(
							hotel.slug,
							this.promotionCodeData()?.promotionCode,
						),
					images: hotel.images,
					secondaryCtaText:
						hotel.toUrl &&
						this.translate.instant('global.goto-hotel-cta'),
					showDisabledText: false,
					socialProof: {
						numberOfRatings: hotel?.rating?.numberOfRatings,
						score: hotel?.rating?.score,
					},
					title: hotel.name,
					iconList: [],
				};
			});
		}
		return [];
	});

	readonly faqItems = [
		{
			title: this.translate.instant(
				'storyblok.pages.promotion-code.faq.question-1.title',
			),
			content: this.translate.instant(
				'storyblok.pages.promotion-code.faq.question-1.content',
			),
		},
		{
			title: this.translate.instant(
				'storyblok.pages.promotion-code.faq.question-2.title',
			),
			content: this.translate.instant(
				'storyblok.pages.promotion-code.faq.question-2.content',
			),
		},
		{
			title: this.translate.instant(
				'storyblok.pages.promotion-code.faq.question-3.title',
			),
			content: this.translate.instant(
				'storyblok.pages.promotion-code.faq.question-3.content',
			),
		},
	];

	promoCodesHotelTitle = [
		{
			_uid: '',
			size: 'md',
			title: this.translate.instant('global.participating-hotels'),
			component: 'runningHead',
		},
	] as unknown as ISbStoryBlok[];

	isBrowser: boolean = false;
	isServer: boolean = false;
	components = Components;

	constructor() {
		super();

		this.isBrowser = isPlatformBrowser(this.platformId);
		this.isServer = isPlatformServer(this.platformId);

		dayjs.extend(isSameOrAfter);

		this.pageSettingsStore.updateState({ negativeHeader: false });
	}

	ngOnInit() {
		if (this.isBrowser) {
			firstValueFrom(this.activatedRoute.queryParams.pipe(take(1))).then(
				(params) => {
					const promoCodeParam = params['promotionCode'];
					if (promoCodeParam) {
						this.promotionCode.set(promoCodeParam);
						// Trigger form submission after view init
						setTimeout(() => {
							this.promoFormComponent()?.onSubmit();
						});
					}
				},
			);
		}
	}

	@HostListener('window:popstate')
	onPopState() {
		if (this.isBrowser && this.promotionCode()) {
			// Check if we navigated back to a URL without the promotionCode param
			const url = new URL(this.document.location.href);
			const hasPromoParam = url.searchParams.has('promotionCode');

			if (!hasPromoParam && this.promotionCodeData()) {
				// Clear the promotion code data to show the form again
				this.promotionCodeData.set(undefined);
			}
		}
	}

	onPromotionCodeData(promotionCodeData: PromotionCodeInterface) {
		const isBookable =
			promotionCodeData?.visible?.from &&
			dayjs().isSameOrAfter(dayjs(promotionCodeData.visible.from));

		if (isBookable && promotionCodeData?.hotels?.length === 1) {
			RouteHelper.redirectToExternalUrl(
				this.getBookingUrl(
					promotionCodeData.hotels[0].slug,
					promotionCodeData.promotionCode,
				),
			);
		} else if (promotionCodeData) {
			this.setPromotionQueryParam(promotionCodeData.promotionCode);
			this.promotionCodeData.set(promotionCodeData);
			this.promotionCode.set(promotionCodeData.promotionCode);
		} else {
			this.promotionCodeData.set(undefined);
		}
	}

	setPromotionQueryParam(promoCode: string) {
		if (this.isBrowser && promoCode) {
			// Set query param of promotionCode=<promoCode> and create a new history entry
			// so users can go back to see the input field again
			const url = new URL(this.document.location.href);
			url.searchParams.set('promotionCode', promoCode);
			history.pushState(null, '', url.pathname + url.search + url.hash);
		}
	}

	private getBookingUrl(hotelSlug: string, promotionCode: string): string {
		const queryParams = {
			promotionCode,
			languageCode: this.language(),
		};

		if (this.bookingtool) {
			return BookingRedirectHelper.getBookingToolUrl(
				this.bookingtool,
				hotelSlug,
				queryParams,
			);
		}

		const path =
			this.language() === this.defaultLanguage
				? '/booking'
				: `/${this.language()}/booking`;
		const search = toQueryString(
			BookingRedirectHelper.toQueryParamRecord(queryParams),
		);

		return `${path}?${search}`;
	}
}
